import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Form from 'react-bootstrap/Form'
import { selectTranslations } from '../../services/i18n/i18nSlice'
import { setLoading } from '../../services/global/globalSlice'
import { fetchExports, fetchTriggerExport } from './ExportsApi'
import { fetchSubjects, fetchTopics } from '../QuestionBankScreen/QuestionBankApi'
import ExportRecipientsModal from './_components/ExportRecipientsModal'
import download_img from '../../assets/images/download.svg'

const STATE_BADGE_CLASS = {
    pending: 'bg-secondary',
    completed: 'bg-success',
    failed: 'bg-danger'
}

const BLANK_FILTERS = { users: [], exams: [], groups: [], folder_id: '', subject_id: '', topic_id: '', date_from: '', date_to: '' }

export default function ExportsHomeScreen() {
    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()

    const [exports, setExports] = useState([])
    const pollTimeoutRef = useRef(null)
    const isMountedRef = useRef(true)

    const [exportLevel, setExportLevel] = useState('user')
    const [exportFormat, setExportFormat] = useState('csv')
    const [filters, setFilters] = useState(BLANK_FILTERS)
    const [subjects, setSubjects] = useState([])
    const [topics, setTopics] = useState([])
    const [showRecipientsModal, setShowRecipientsModal] = useState(false)

    const topicsForFilter = useMemo(() => {
        if (!filters.subject_id) return topics
        return topics.filter(topic => topic.subject_id === filters.subject_id)
    }, [filters.subject_id, topics])

    useEffect(() => {
        Promise.all([fetchSubjects(), fetchTopics()]).then(([subjectsResponse, topicsResponse]) => {
            if (subjectsResponse.success) setSubjects(subjectsResponse.data || [])
            if (topicsResponse.success) setTopics(topicsResponse.data || [])
        })
    }, [])

    const loadExports = async () => {
        const response = await fetchExports()
        if (!isMountedRef.current) return
        if (response.success) {
            setExports(response.data || [])
            if (response.data?.some(exp => exp.state === 'pending')) {
                pollTimeoutRef.current = setTimeout(loadExports, 8000)
            }
        } else {
            toast.error(t.error.error_loading)
        }
    }

    useEffect(() => {
        isMountedRef.current = true
        dispatch(setLoading(true))
        loadExports().then(() => dispatch(setLoading(false)))
        return () => {
            isMountedRef.current = false
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
        }
    }, [])

    const handleLevelChange = (level) => {
        setExportLevel(level)
        setFilters(BLANK_FILTERS)
    }

    const handleApplyRecipients = ({ users, exams, groups }) => {
        setFilters({ ...filters, users, exams, groups })
        setShowRecipientsModal(false)
    }

    const handleTriggerExport = async () => {
        dispatch(setLoading(true))
        const request_filters = {}
        if (exportLevel === 'exam') {
            if (filters.exams?.length) request_filters.exam_id = filters.exams.map(e => e.exam_id)
            if (filters.users?.length) request_filters.user_id = filters.users.map(u => u.user_id)
            if (filters.groups?.length) request_filters.group_id = filters.groups.map(g => g.group_id)
        } else if (exportLevel === 'training') {
            if (filters.folder_id) request_filters.folder_id = filters.folder_id
            if (filters.users?.length) request_filters.user_id = filters.users.map(u => u.user_id)
            if (filters.groups?.length) request_filters.group_id = filters.groups.map(g => g.group_id)
        }
        if (exportLevel === 'exam' || exportLevel === 'training') {
            if (filters.topic_id) request_filters.topic_id = filters.topic_id
            else if (filters.subject_id) request_filters.subject_id = filters.subject_id
            if (filters.date_from) request_filters.date_from = new Date(filters.date_from).toISOString()
            if (filters.date_to) request_filters.date_to = new Date(filters.date_to + 'T23:59:59.999').toISOString()
        }
        const response = await fetchTriggerExport(exportLevel, exportFormat, Object.keys(request_filters).length ? request_filters : null)
        if (response.success) {
            toast.success(t.exports.export_triggered)
            setFilters(BLANK_FILTERS)
            setExportFormat('csv')
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
            await loadExports()
        } else {
            toast.error(t.error.error_loading)
        }
        dispatch(setLoading(false))
    }

    const countOf = (value) => Array.isArray(value) ? value.length : (value ? 1 : 0)
    const asArray = (value) => value == null ? [] : (Array.isArray(value) ? value : [value])

    const formatUser = (u) => {
        const name = `${u?.firstname || ''} ${u?.lastname || ''}`.trim()
        return name || u?.nickname || u?.email || u?.user_id
    }
    const formatExam = (e) => e?.title || e?.exam_id
    const formatTopic = (top) => top?.topic_name || top?.topic_id
    const formatSubject = (s) => s?.subject_name || s?.subject_id
    const formatFolder = (f) => f?.name || f?.folder_id

    const formatList = (items, formatter, max = 3) => {
        const labels = items.map(formatter).filter(Boolean)
        if (!labels.length) return ''
        if (labels.length <= max) return labels.join(', ')
        return `${labels.slice(0, max).join(', ')} +${labels.length - max}`
    }

    const renderFilters = (exp) => {
        const f = exp.filters || {}
        const parts = []

        const exams = asArray(f.exam_data)
        if (exams.length) parts.push(`${t.exports.level_exam}: ${formatList(exams, formatExam)}`)
        else if (countOf(f.exam_id)) parts.push(`${countOf(f.exam_id)} ${t.exports.level_exam.toLowerCase()}`)

        const folders = asArray(f.folder_data)
        if (folders.length) parts.push(`${t.folder.folder}: ${formatList(folders, formatFolder)}`)
        else if (f.folder_id) parts.push(`${t.folder.folder}: ${f.folder_id}`)

        const users = asArray(f.user_data)
        if (users.length) parts.push(`${t.permissions.single_students}: ${formatList(users, formatUser)}`)
        else if (countOf(f.user_id)) parts.push(`${countOf(f.user_id)} ${t.permissions.single_students.toLowerCase()}`)

        const groupCount = countOf(f.group_id)
        if (groupCount) parts.push(`${groupCount} ${t.permissions.groups.toLowerCase()}`)

        const subjects = asArray(f.subject_data)
        if (subjects.length) parts.push(`${t.question_bank.subject}: ${formatList(subjects, formatSubject)}`)
        else if (f.subject_id) parts.push(`${t.question_bank.subject}: ${f.subject_id}`)

        const topics = asArray(f.topic_data)
        if (topics.length) parts.push(`${t.question_bank.topic}: ${formatList(topics, formatTopic)}`)
        else if (f.topic_id) parts.push(`${t.question_bank.topic}: ${f.topic_id}`)

        if (f.date_from) parts.push(`${t.exports.date_from}: ${new Date(f.date_from).toLocaleDateString('it-IT')}`)
        if (f.date_to) parts.push(`${t.exports.date_to}: ${new Date(f.date_to).toLocaleDateString('it-IT')}`)

        if (!parts.length) return t.exports.no_filters
        return parts.join(' · ')
    }

    return (
        <section id="exportsScreen">
            <div className="page_title">
                <img src={download_img} alt="" />
                <h1>{t.exports.page_title}</h1>
            </div>

            <div className="info_card mt-3">
                <h2>{t.exports.new_export}</h2>

                <div className="mt-3 d-flex flex-wrap">
                    <Form.Group style={{ maxWidth: 300 }} className="me-4" controlId="export_level">
                        <Form.Label className="fw-bold">{t.exports.level}</Form.Label>
                        <Form.Select value={exportLevel} onChange={(e) => handleLevelChange(e.target.value)}>
                            <option value="user">{t.exports.level_user}</option>
                            <option value="exam">{t.exports.level_exam}</option>
                            <option value="training">{t.exports.level_training}</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group style={{ maxWidth: 300 }} controlId="export_format">
                        <Form.Label className="fw-bold">{t.exports.format}</Form.Label>
                        <Form.Select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
                            <option value="csv">{t.exports.format_csv}</option>
                            <option value="excel">{t.exports.format_excel}</option>
                        </Form.Select>
                    </Form.Group>
                </div>

                {exportLevel === 'training' ?
                    <div className="mt-3 pt-3 border-top">
                        <Form.Group style={{ maxWidth: 300 }} controlId="export_folder_filter">
                            <Form.Label className="fw-bold">{t.exports.folder_filter}</Form.Label>
                            <Form.Control
                                type="text"
                                value={filters.folder_id}
                                placeholder={t.exports.folder_filter_placeholder}
                                onChange={(e) => setFilters({ ...filters, folder_id: e.target.value })}
                            />
                        </Form.Group>
                    </div>
                : null}

                {exportLevel === 'exam' || exportLevel === 'training' ?
                    <div className="mt-3 pt-3 border-top">
                        <Form.Label className="fw-bold d-block">{t.exports.recipients_filter}</Form.Label>
                        <div className="d-flex align-items-center flex-wrap">
                            <button type="button" className="secondary mb-2" onClick={() => setShowRecipientsModal(true)}>
                                {t.exports.select_recipients}
                            </button>
                            {filters.users.length || filters.exams.length || filters.groups.length ?
                                <span className="ms-2 mb-2 text-muted">
                                    {[
                                        filters.exams.length ? `${filters.exams.length} ${t.exports.level_exam.toLowerCase()}` : null,
                                        filters.users.length ? `${filters.users.length} ${t.permissions.single_students.toLowerCase()}` : null,
                                        filters.groups.length ? `${filters.groups.length} ${t.permissions.groups.toLowerCase()}` : null
                                    ].filter(Boolean).join(' · ')}
                                </span>
                            : null}
                        </div>
                    </div>
                : null}

                {exportLevel === 'exam' || exportLevel === 'training' ?
                    <div className="mt-3 pt-3 border-top">
                        <Form.Label className="fw-bold d-block">{t.exports.subject_topic_filter}</Form.Label>
                        <div className="d-flex align-items-end flex-wrap">
                            <Form.Group className="mb-2 me-2" controlId="export_subject_filter">
                                <Form.Label>{t.exports.subject_filter}</Form.Label>
                                <Form.Select
                                    value={filters.subject_id}
                                    onChange={(e) => setFilters({ ...filters, subject_id: e.target.value, topic_id: '' })}
                                >
                                    <option value="">{t.question_bank.all_subjects}</option>
                                    {subjects?.map(s => (
                                        <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-2 me-2" controlId="export_topic_filter">
                                <Form.Label>{t.exports.topic_filter}</Form.Label>
                                <Form.Select
                                    value={filters.topic_id}
                                    onChange={(e) => setFilters({ ...filters, topic_id: e.target.value })}
                                >
                                    <option value="">{t.question_bank.all_topics}</option>
                                    {topicsForFilter?.map(topic => (
                                        <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>
                : null}

                {exportLevel === 'exam' || exportLevel === 'training' ?
                    <div className="mt-3 pt-3 border-top">
                        <Form.Label className="fw-bold d-block">{t.exports.date_range_filter}</Form.Label>
                        <div className="d-flex align-items-end flex-wrap">
                            <Form.Group className="mb-2 me-2" controlId="export_date_from_filter">
                                <Form.Label>{t.exports.date_from}</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={filters.date_from}
                                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-2 me-2" controlId="export_date_to_filter">
                                <Form.Label>{t.exports.date_to}</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={filters.date_to}
                                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                                />
                            </Form.Group>
                        </div>
                    </div>
                : null}

                <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center flex-wrap">
                    {exportLevel !== 'user' ?
                        <p className="text-muted mb-2"><small>{t.exports.filters_hint}</small></p>
                    : <span></span>}
                    <button className="mb-2" onClick={handleTriggerExport}>
                        {t.exports.trigger_export}
                    </button>
                </div>
            </div>

            <div className="table-responsive mt-3">
                <table className="table">
                    <thead>
                        <tr>
                            <th>{t.exports.level}</th>
                            <th>{t.exports.filters}</th>
                            <th>{t.exports.format}</th>
                            <th>{t.exports.state}</th>
                            <th>{t.exports.created_date}</th>
                            <th>{t.exports.completion_date}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {exports?.length ? exports.map((exp) => (
                            <tr key={exp.export_id}>
                                <td>{t.exports[`level_${exp.level}`] || exp.level}</td>
                                <td>{renderFilters(exp)}</td>
                                <td>{t.exports[`format_${exp.format}`] || exp.format}</td>
                                <td><span className={`badge ${STATE_BADGE_CLASS[exp.state] || 'bg-secondary'}`}>{t.exports[`state_${exp.state}`] || exp.state}</span></td>
                                <td>{exp.created_date ? new Date(exp.created_date).toLocaleString('it-IT') : ''}</td>
                                <td>{exp.completion_date ? new Date(exp.completion_date).toLocaleString('it-IT') : ''}</td>
                                <td>
                                    {exp.state === 'completed' && exp.file_url ?
                                        <a href={exp.file_url} target="_blank" rel="noreferrer">{t.exports.download}</a>
                                    : null}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={7} className="text-center">{t.exports.no_exports}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ExportRecipientsModal
                show={showRecipientsModal}
                showExams={exportLevel === 'exam'}
                selectedUsers={filters.users}
                selectedExams={filters.exams}
                selectedGroups={filters.groups}
                onApply={handleApplyRecipients}
                onClose={() => setShowRecipientsModal(false)}
            />
        </section>
    )
}
